<template>
  <input ref="element" v-bind="bindings" class="c-input" />
</template>

<script setup lang="ts">
  import IMask, { InputMask, Masked } from 'imask';
  import { computed, ComputedRef, onBeforeUnmount, onMounted, watch } from 'vue';
  import { elementRef } from '../../utils/component';

  export type Mask = string | object; // can't find better types for iMask

  let iMask: InputMask<any> | undefined;

  const props = defineProps<{
    type: string;
    modelValue: any;
    mask?: Mask;
    keepMask?: boolean;
    maxlength?: number;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', v: any): void;
  }>();

  const element = elementRef<HTMLInputElement>();

  const maskOptions = computed(() => {
    if (props.keepMask && props.type !== 'text') throw new Error('mask can only be kept with text fields');
    if (props.type === 'amount') {
      if (props.mask) throw new Error('no custom mask allowed on amounts');
      return {
        mask: Number, // enable number mask
        scale: 2, // digits after point, 0 for integers
        thousandsSeparator: '.', // any single char
        padFractionalZeros: true, // if true, then pads zeros at end to the length of scale
        radix: ',', // fractional delimiter
      };
    }
    if (!props.mask) return;
    if (!['text', 'number'].includes(props.type)) throw new Error(`mask is not available on ${props.type} fields`);
    if (typeof props.mask === 'string') return { mask: props.mask };
    if (typeof props.mask === 'object') return props.mask;
    throw new Error('invalid mask');
  }) as ComputedRef<Masked<any> | undefined>;

  const bindings = computed(() => {
    // if mask is active, value + input are handled by iMask!
    if (maskOptions.value) {
      return {
        type: 'text',
      };
    } else {
      return {
        type: props.type,
        value: props.modelValue,
        onInput: (e: any) => emitValue(e.target.value),
        maxlength: props.maxlength,
      };
    }
  });

  onMounted(setupMask);
  onBeforeUnmount(destroyMask);
  watch(maskOptions, setupMask, { deep: true });
  watch(() => props.keepMask, emitMaskValue);
  watch(() => props.modelValue, updateMaskValue);

  function emitMaskValue() {
    emitValue((props.keepMask ? iMask?.value : iMask?.unmaskedValue) ?? null);
  }

  function emitValue(value: any) {
    emit('update:modelValue', transformValue(value));
  }

  function transformValue(source: any) {
    switch (props.type) {
      case 'number':
      case 'amount':
        return source !== '' ? Number(source) : null;
      default:
        return source;
    }
  }

  function setupMask() {
    destroyMask();
    if (!maskOptions.value || !element.value) return;

    iMask = IMask(element.value, maskOptions.value) as InputMask<any>;

    iMask.on('accept', emitMaskValue);

    updateMaskValue();
    // emit value in case the mask of an existing field and value was changed
    emitMaskValue();
  }

  function updateMaskValue() {
    if (!iMask) return;
    iMask[props.keepMask ? 'value' : 'unmaskedValue'] = `${props.modelValue ?? ''}`;
  }

  function destroyMask() {
    iMask?.destroy();
    iMask = undefined;
  }
</script>

<style lang="scss">
.c-input {
  outline: none;
  border: none;
  background-color: transparent;
  font-family: inherit;
  font-weight: inherit;
  cursor: inherit;
  min-width: 0;
  font-size: 1rem;
  line-height: 1.125;
  color: $neutral-800;

  &::placeholder {
    color: $input-placeholder;
    font-weight: 400;
  }

  .c-field-control:has(&) {
    cursor: text;
  }
}
</style>
